import {
  type QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useNavigate } from "react-router";

import { api, isApiError, setCsrfToken, unwrap } from "@/api/client";
import type { Me, Organization, Role } from "@/api/types";
import { paths } from "@/routes/paths";

export const meQueryKey = ["me"] as const;

/** `GET /v1/me`; `null` si no hay sesión (401). */
export const fetchMe = async (): Promise<Me | null> => {
  try {
    const me = await unwrap(api.GET("/v1/me"));
    setCsrfToken(me.csrfToken);
    return me;
  } catch (error) {
    if (isApiError(error) && error.status === 401) {
      setCsrfToken(null);
      return null;
    }
    throw error;
  }
};

export const useMe = () =>
  useQuery({
    queryKey: meQueryKey,
    queryFn: fetchMe,
    staleTime: 5 * 60_000,
    retry: false,
  });

export interface Session {
  me: Me;
  /** Organización de la sesión (`null` si aún no ha elegido). */
  currentOrganization: Organization | null;
  role: Role | null;
  isAdmin: boolean;
  isPlatformAdmin: boolean;
}

export const toSession = (me: Me): Session => {
  const currentOrganization = me.organization ?? null;
  const role = currentOrganization ? (me.role ?? null) : null;
  return {
    me,
    currentOrganization,
    role,
    isAdmin: role === "admin",
    isPlatformAdmin: me.isPlatformAdmin,
  };
};

/** Vacía la sesión en caché: las guardas llevan al login. */
export const clearSession = (queryClient: QueryClient) => {
  setCsrfToken(null);
  queryClient.removeQueries({
    predicate: (query) => query.queryKey[0] !== meQueryKey[0],
  });
  queryClient.setQueryData(meQueryKey, null);
};

export const useLogout = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: () => unwrap(api.POST("/v1/auth/logout")),
    onSettled: () => {
      clearSession(queryClient);
      void navigate(paths.login, { replace: true });
    },
    meta: { silentError: true },
  });
};

export const useSwitchOrganization = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (organizationId: string) =>
      unwrap(
        api.POST("/v1/auth/switch-organization", {
          body: { organizationId },
        }),
      ),
    onSuccess: (me) => {
      // Todo lo cacheado es de la organización anterior. La respuesta ya es el
      // `Me` de la nueva sesión (con el CSRF rotado).
      queryClient.removeQueries({
        predicate: (query) => query.queryKey[0] !== meQueryKey[0],
      });
      setCsrfToken(me.csrfToken);
      queryClient.setQueryData(meQueryKey, me);
      void navigate(paths.home, { replace: true });
    },
  });
};
